import { parseTime, Time } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPUT } from "api/tanStackQuery/helpers";
import { isBefore } from "date-fns";
import {
  getArrivalTimeStr,
  getDepartureTimeStr,
  isAfterOrEqual,
  isBeforeOrEqual,
  isTimeValidComparedTo,
  parseTimeAndApplyMidnightCheck,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/utils";
import { FC, FormEvent, useEffect } from "react";
import { ActionModal } from "shared/components/ActionModal";
import { TimePicker, tokens } from "shared/components/Spor";
import { useTypedForm } from "shared/hooks/useTypedForm";
import {
  AlternativeTransportItineraryStop,
  StopReference,
} from "shared/types/alternativeTransport";
import styled, { css, useTheme } from "styled-components";

//* Formats Time to HH:MM */
const formatTime = (time?: Time) => {
  if (!time) return "";
  return `${time.hour.toString().padStart(2, "0")}:${time.minute
    .toString()
    .padStart(2, "0")}`;
};

const toIsoOrUndefined = (timeStr: string, travelDate: Date) =>
  timeStr !== ""
    ? parseTimeAndApplyMidnightCheck(timeStr, travelDate).toISOString()
    : undefined;

const toTimeValueOrNull = (timeStr: string) =>
  timeStr === "" ? null : parseTime(timeStr);

const ItemContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${tokens.default.size.spacing[2]};
`;

type ChangeStopForm = {
  estimatedArrivalTime: string;
  estimatedDepartureTime: string;
};

// Payload for the vehicle-updates endpoint, distinct from the actual-time PATCH schema
type EstimatedStopTimeUpdatePayload = {
  vehicleUuid: string;
  type: "ESTIMATED_STOP_TIME";
  stopReference: StopReference;
  stopName: string;
  distributionStopReferences: Array<StopReference>;
  estimatedArrivalTime?: string;
  estimatedDepartureTime?: string;
};

type Props = {
  id: string;
  showModal: boolean;
  travelDate: Date;
  stop: AlternativeTransportItineraryStop | null;
  distributionStopReferences: Array<StopReference>;
  closeModal: () => void;
};

export const ChangeEstimatedTime: FC<Props> = ({
  id,
  travelDate,
  showModal,
  stop,
  distributionStopReferences,
  closeModal,
}) => {
  const theme = useTheme();

  const {
    mutate,
    status,
    reset: resetMutation,
  } = useMutation({
    mutationKey: ["updateEstimatedStopTimes", id],
    mutationFn: (payload: EstimatedStopTimeUpdatePayload) =>
      mutationFnPUT(
        `${getBackendUrl()}/alternative-transport/vehicle-updates`,
        payload,
      ),
    onSuccess: () => {
      closeModal();
    },
  });

  const {
    values,
    handleChange,
    reset: resetForm,
    startValidation,
    errors: formErrors,
  } = useTypedForm<ChangeStopForm>(
    {
      estimatedArrivalTime: "",
      estimatedDepartureTime: "",
    },
    ({ estimatedArrivalTime, estimatedDepartureTime }) => ({
      estimatedArrivalTime: isTimeValidComparedTo(
        estimatedArrivalTime,
        estimatedDepartureTime,
        travelDate,
        isBeforeOrEqual,
        "Ankomsttid må være før avgangstid eller la feltet stå tomt",
      ),
      estimatedDepartureTime: isTimeValidComparedTo(
        estimatedDepartureTime,
        estimatedArrivalTime,
        travelDate,
        isAfterOrEqual,
        "Avgangstid må være etter ankomsttid eller la feltet stå tomt",
      ),
    }),
  );

  // We update "default values" every time we change stop due to re-using one modal for all stops
  useEffect(() => {
    if (stop) {
      handleChange({
        estimatedArrivalTime: getArrivalTimeStr(stop),
        estimatedDepartureTime: getDepartureTimeStr(stop),
      });
    }
  }, [stop]);

  const onClose = () => {
    resetMutation();
    resetForm();
    closeModal();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    startValidation((state) => {
      if (state === "valid" && stop !== null) {
        const { estimatedArrivalTime, estimatedDepartureTime } = values;
        const payload: EstimatedStopTimeUpdatePayload = {
          vehicleUuid: id,
          type: "ESTIMATED_STOP_TIME",
          stopReference: stop.stopReference,
          stopName: stop.name ?? "ukjent stoppested",
          distributionStopReferences,
          estimatedArrivalTime: toIsoOrUndefined(
            estimatedArrivalTime,
            travelDate,
          ),
          estimatedDepartureTime: toIsoOrUndefined(
            estimatedDepartureTime,
            travelDate,
          ),
        };
        mutate(payload);
      }
    });
  };

  const onArrivalTimeChangeBlur = () => {
    const { estimatedArrivalTime, estimatedDepartureTime } = values;
    const estimatedArrival = parseTimeAndApplyMidnightCheck(
      estimatedArrivalTime,
      travelDate,
    );
    const estimatedDeparture = parseTimeAndApplyMidnightCheck(
      estimatedDepartureTime,
      travelDate,
    );
    // if departure time is before arrival time, we update it
    if (isBefore(estimatedDeparture, estimatedArrival)) {
      handleChange({ estimatedDepartureTime: estimatedArrivalTime });
    }
    startValidation();
  };

  const arrivalTimeValue = toTimeValueOrNull(values.estimatedArrivalTime);
  const departureTimeValue = toTimeValueOrNull(values.estimatedDepartureTime);

  const arrivalLaterThanDeparture = isBefore(
    parseTimeAndApplyMidnightCheck(values.estimatedDepartureTime, travelDate),
    parseTimeAndApplyMidnightCheck(values.estimatedArrivalTime, travelDate),
  );

  return showModal && stop !== null ? (
    <ActionModal
      title="Rediger estimert avgangs- og ankomsttid"
      actionTitle="Lagre"
      onClose={onClose}
      onSubmit={onSubmit}
      loading={status === "pending"}
      isSuccess={status === "success"}
      isError={status === "error"}
      size="md"
      successMessage="Oppdatering av tidspunkter for stoppested vellykket"
      failureMessage="Kunne ikke oppdatere avgangstider for avgang. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
      contentBodyStyle={[
        css`
          background-color: ${theme.colorBackgroundMain};
          padding: ${tokens.default.size.spacing[3]};
        `,
        arrivalLaterThanDeparture &&
          css`
            padding-bottom: ${tokens.default.size
              .spacing[9]}; /* Add extra padding to accommodate error message */
          `,
      ]}
    >
      <ItemContent>
        <TimePicker
          label="Ankomsttid"
          name="estimatedArrivalTime"
          disabled={values.estimatedArrivalTime === ""}
          minuteInterval={1}
          value={arrivalTimeValue}
          onValueChange={(time) => {
            handleChange({ estimatedArrivalTime: formatTime(time) });
          }}
          width="100%"
          onBlur={() => onArrivalTimeChangeBlur()}
          aria-labelledby="estimatedArrivalTime-label"
          aria-invalid={formErrors.estimatedArrivalTime !== undefined}
          aria-errormessage="estimatedArrivalTime-error-message"
          invalid={arrivalLaterThanDeparture}
          errorText={
            arrivalLaterThanDeparture
              ? "Ankomst må være tidligere enn avgang"
              : undefined
          }
        />
        <TimePicker
          label="Avgangstid"
          name="estimatedDepartureTime"
          disabled={values.estimatedDepartureTime === ""}
          minuteInterval={1}
          value={departureTimeValue}
          onValueChange={(time) => {
            handleChange({ estimatedDepartureTime: formatTime(time) });
          }}
          width="100%"
          onBlur={() => startValidation()}
          aria-labelledby="estimatedDepartureTime-label"
          aria-invalid={formErrors.estimatedDepartureTime !== undefined}
          aria-errormessage="estimatedDepartureTime-error-message"
          invalid={arrivalLaterThanDeparture}
        />
      </ItemContent>
    </ActionModal>
  ) : null;
};
